import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Shield, Lock, Eye, Heart, Users, Clock, Star, Zap, Globe, Award } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Shield,
  Lock,
  Eye,
  Heart,
  Users,
  Clock,
  Star,
  Zap,
  Globe,
  Award,
};

// Feature Card Component with all premium animations
const FeatureCard = ({ feature, index, isVisible }) => {
  const IconComponent = iconMap[feature.icon] || Shield;
  const cardRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [isHovered, setIsHovered] = useState(false);
  const rafRef = useRef(null);

  // Handle mouse move for spotlight effect
  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current) return;
    
    // Cancel any pending RAF
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    
    rafRef.current = requestAnimationFrame(() => {
      const rect = cardRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setMousePosition({ x, y });
    });
  }, []);

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => {
    setIsHovered(false);
    // Reset spotlight to center on leave
    setMousePosition({ x: 50, y: 50 });
  };

  // Cleanup RAF on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={cardRef}
      className={`feature-card ${isVisible ? 'is-visible' : ''}`}
      style={{
        '--delay': `${index * 130}ms`,
        '--mouse-x': `${mousePosition.x}%`,
        '--mouse-y': `${mousePosition.y}%`,
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid={`feature-card-${index}`}
    >
      {/* Gold Border Glow Overlay */}
      <div className="feature-card-glow" />
      
      {/* Cursor Spotlight Overlay */}
      <div className={`feature-card-spotlight ${isHovered ? 'is-active' : ''}`} />
      
      {/* Card Content */}
      <div className="feature-card-content">
        {/* Icon */}
        <div className="feature-icon-wrapper">
          <IconComponent className="feature-icon" />
        </div>

        {/* Content */}
        <h4 className="feature-title">
          {feature.title}
        </h4>
        <p className="feature-description">
          {feature.description}
        </p>
      </div>
    </div>
  );
};

const FeaturesSection = () => {
  const [features, setFeatures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [visibleCards, setVisibleCards] = useState(new Set());
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setFeatures(response.data.features || []);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
        // Fallback to default features
        setFeatures([
          { id: '1', icon: 'Shield', title: 'Bank-Grade Security', description: 'Your sensitive information is protected with military-grade encryption and strict privacy protocols.' },
          { id: '2', icon: 'Lock', title: 'Secure Digital Vault', description: 'Store all your important documents, assets, and wishes in an encrypted vault with controlled access.' },
          { id: '3', icon: 'Eye', title: 'Complete Transparency', description: 'Every step is clear and traceable. Your nominees are always informed about the process.' },
          { id: '4', icon: 'Heart', title: 'Compassionate Support', description: 'We handle sensitive matters with empathy, ensuring dignity and respect throughout.' },
          { id: '5', icon: 'Users', title: 'Verified Legal Partners', description: 'All our legal advisors and partners are thoroughly vetted professionals you can trust.' },
          { id: '6', icon: 'Clock', title: '24/7 Accessibility', description: 'Access your vault and documents anytime, anywhere. Your legacy is always at your fingertips.' },
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Intersection Observer for staggered reveal
  useEffect(() => {
    if (features.length === 0) return;

    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = parseInt(entry.target.dataset.cardIndex, 10);
          setVisibleCards(prev => {
            const newSet = new Set(prev);
            newSet.add(index);
            return newSet;
          });
          // Unobserve after triggering (trigger once only)
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observe all cards after a small delay
    const timeoutId = setTimeout(() => {
      cardRefs.current.forEach((ref) => {
        if (ref) observer.observe(ref);
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      cardRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [features]);

  if (isLoading) {
    return (
      <section id="features" className="py-24 bg-slate-900">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400"></div>
        </div>
      </section>
    );
  }

  if (features.length === 0) {
    return null;
  }

  return (
    <section id="features" ref={sectionRef} className="py-24 bg-slate-900">
      <style>{`
        /* ===== BASE CARD STYLES ===== */
        .feature-card {
          position: relative;
          background: rgba(30, 41, 59, 0.5);
          backdrop-filter: blur(8px);
          border-radius: 1rem;
          padding: 2rem;
          border: 1px solid rgba(71, 85, 105, 0.5);
          overflow: hidden;
          
          /* Initial hidden state for entrance animation */
          opacity: 0;
          transform: translateY(20px) scale(0.975);
          transition: 
            opacity 650ms cubic-bezier(0.22, 1, 0.36, 1),
            transform 650ms cubic-bezier(0.22, 1, 0.36, 1),
            border-color 260ms ease-out,
            box-shadow 260ms ease-out;
          transition-delay: var(--delay, 0ms);
        }
        
        /* Visible state (after scroll trigger) */
        .feature-card.is-visible {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
        
        /* ===== HOVER LIFT + SHADOW BLOOM ===== */
        .feature-card:hover {
          transform: translateY(-8px) scale(1.01);
          box-shadow: 
            0 20px 40px rgba(0, 0, 0, 0.3),
            0 8px 16px rgba(0, 0, 0, 0.2),
            0 0 0 1px rgba(245, 158, 11, 0.1);
          border-color: rgba(245, 158, 11, 0.3);
          transition: 
            transform 260ms ease-out,
            box-shadow 260ms ease-out,
            border-color 260ms ease-out;
        }
        
        .feature-card:not(:hover) {
          transition: 
            transform 300ms ease-out,
            box-shadow 300ms ease-out,
            border-color 300ms ease-out;
        }
        
        /* ===== GOLD BORDER GLOW (::before) ===== */
        .feature-card-glow {
          position: absolute;
          inset: -1px;
          border-radius: inherit;
          padding: 1px;
          background: linear-gradient(
            135deg,
            rgba(251, 191, 36, 0) 0%,
            rgba(245, 158, 11, 0) 50%,
            rgba(217, 119, 6, 0) 100%
          );
          -webkit-mask: 
            linear-gradient(#fff 0 0) content-box, 
            linear-gradient(#fff 0 0);
          mask: 
            linear-gradient(#fff 0 0) content-box, 
            linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          opacity: 0;
          transition: opacity 250ms ease-out, background 250ms ease-out;
          pointer-events: none;
          z-index: 1;
        }
        
        .feature-card:hover .feature-card-glow {
          opacity: 1;
          background: linear-gradient(
            135deg,
            rgba(251, 191, 36, 0.6) 0%,
            rgba(245, 158, 11, 0.4) 50%,
            rgba(217, 119, 6, 0.6) 100%
          );
        }
        
        /* ===== CURSOR SPOTLIGHT (::after) ===== */
        .feature-card-spotlight {
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: radial-gradient(
            circle at var(--mouse-x, 50%) var(--mouse-y, 50%),
            rgba(251, 191, 36, 0.15) 0%,
            rgba(245, 158, 11, 0.08) 25%,
            transparent 55%
          );
          opacity: 0;
          transition: opacity 180ms ease-out;
          pointer-events: none;
          z-index: 2;
        }
        
        .feature-card-spotlight.is-active {
          opacity: 1;
        }
        
        /* ===== CARD CONTENT ===== */
        .feature-card-content {
          position: relative;
          z-index: 3;
        }
        
        /* ===== ICON STYLES ===== */
        .feature-icon-wrapper {
          width: 3.5rem;
          height: 3.5rem;
          border-radius: 0.75rem;
          background: linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.1) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
          transition: 
            background 220ms ease-out,
            box-shadow 220ms ease-out;
        }
        
        .feature-card:hover .feature-icon-wrapper {
          background: linear-gradient(135deg, rgba(245, 158, 11, 0.3) 0%, rgba(217, 119, 6, 0.2) 100%);
          box-shadow: 0 0 20px rgba(251, 191, 36, 0.25);
        }
        
        .feature-icon {
          width: 1.75rem;
          height: 1.75rem;
          color: #fbbf24;
          transition: transform 220ms ease-out, filter 220ms ease-out;
        }
        
        .feature-card:hover .feature-icon {
          transform: scale(1.08) rotate(-4deg);
          filter: drop-shadow(0 0 8px rgba(251, 191, 36, 0.4));
        }
        
        /* ===== TITLE STYLES ===== */
        .feature-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #ffffff;
          margin-bottom: 0.75rem;
          letter-spacing: 0;
          transition: color 200ms ease-out, letter-spacing 200ms ease-out;
        }
        
        .feature-card:hover .feature-title {
          color: #fbbf24;
          letter-spacing: 0.2px;
        }
        
        /* ===== DESCRIPTION STYLES ===== */
        .feature-description {
          color: #94a3b8;
          line-height: 1.625;
          font-size: 1rem;
          text-align: justify;
        }
        
        /* ===== REDUCED MOTION ===== */
        @media (prefers-reduced-motion: reduce) {
          .feature-card {
            transition: opacity 300ms ease;
            transform: none !important;
          }
          
          .feature-card.is-visible {
            transform: none !important;
          }
          
          .feature-card:hover {
            transform: none !important;
          }
          
          .feature-card-spotlight {
            /* Static centered highlight instead of following cursor */
            background: radial-gradient(
              circle at 50% 50%,
              rgba(251, 191, 36, 0.1) 0%,
              transparent 60%
            ) !important;
          }
          
          .feature-icon {
            transition: none;
          }
          
          .feature-card:hover .feature-icon {
            transform: none;
          }
        }
        
        /* ===== TOUCH DEVICE FALLBACK ===== */
        @media (hover: none) {
          .feature-card:active {
            transform: translateY(-4px) scale(1.005);
            border-color: rgba(245, 158, 11, 0.3);
          }
          
          .feature-card:active .feature-card-glow {
            opacity: 1;
            background: linear-gradient(
              135deg,
              rgba(251, 191, 36, 0.5) 0%,
              rgba(245, 158, 11, 0.3) 50%,
              rgba(217, 119, 6, 0.5) 100%
            );
          }
          
          .feature-card:active .feature-title {
            color: #fbbf24;
          }
          
          .feature-card-spotlight {
            /* Static centered glow for touch */
            background: radial-gradient(
              circle at 50% 50%,
              rgba(251, 191, 36, 0.1) 0%,
              transparent 60%
            );
          }
          
          .feature-card:active .feature-card-spotlight {
            opacity: 1;
          }
        }
        
        /* ===== FOCUS VISIBLE FOR ACCESSIBILITY ===== */
        .feature-card:focus-visible {
          outline: 2px solid #fbbf24;
          outline-offset: 2px;
          transform: translateY(-4px) scale(1.005);
        }
        
        .feature-card:focus-visible .feature-card-glow {
          opacity: 1;
        }
        
        .feature-card:focus-visible .feature-title {
          color: #fbbf24;
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-sm font-semibold text-amber-400 tracking-wider uppercase mb-4">
            Security & Trust
          </h2>
          <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
            Your peace of mind is our priority
          </h3>
          <p className="text-slate-400 max-w-2xl mx-auto text-lg">
            We understand the sensitivity of estate planning. That's why we've built
            WillKart.ai with uncompromising security and transparency.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div
              key={feature.id || index}
              ref={(el) => (cardRefs.current[index] = el)}
              data-card-index={index}
            >
              <FeatureCard
                feature={feature}
                index={index}
                isVisible={visibleCards.has(index)}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
